import React, { useEffect, useState } from "react";
import clsx from "clsx";
import styles from "./Pagination.module.css";
import { ReactComponent as LeftArrow } from "assets/icons/previous-page-arrow.svg";
import { ReactComponent as NextArrow } from "assets/icons/next-page-arrow.svg";
import { ReactComponent as FirstLastArrow } from "assets/icons/chevron-right-on-button.svg";

interface IPaginationProps {
  paginationData: any;
  numberOfPages: number;
  searchObits: (pageNumber: number) => Promise<void>;
}

const Pagination = ({
  paginationData,
  numberOfPages,
  searchObits,
}: IPaginationProps) => {
  const [pageNumbers, setPageNumbers] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    const activePageInfo = paginationData?.find((page) => page.active);
    const currPage = !!activePageInfo?.label ? Number(activePageInfo.label) : 1;
    setCurrentPage(currPage);
  }, [paginationData]);

  useEffect(() => {
    if (numberOfPages > 2) {
      if (currentPage === 1) {
        setPageNumbers([currentPage, currentPage + 1, currentPage + 2]);
      } else if (currentPage > 1 && currentPage < numberOfPages) {
        setPageNumbers([currentPage - 1, currentPage, currentPage + 1]);
      } else if (currentPage === numberOfPages) {
        setPageNumbers([currentPage - 1, currentPage]);
      }
    } else {
      setPageNumbers([...Array.from(Array(numberOfPages + 1).keys())].slice(1));
    }
  }, [currentPage, numberOfPages]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentPage]);

  const handlePageChange = (pageNumber: number) => {
    searchObits(pageNumber);
    setCurrentPage(pageNumber);
  };

  const toFirstPage = () => {
    searchObits(1);
    setCurrentPage(1);
  };

  const toPrevPage = () => {
    if (currentPage > 1) {
      searchObits(currentPage - 1);
      setCurrentPage(currentPage - 1);
    }
  };

  const toNextPage = () => {
    if (currentPage < numberOfPages) {
      searchObits(currentPage + 1);
      setCurrentPage(currentPage + 1);
    }
  };

  const toLastPage = () => {
    searchObits(numberOfPages);
    setCurrentPage(numberOfPages);
  };

  return (
    <>
      {numberOfPages > 0 && (
        <nav className={styles.paginationOuterWrapper}>
          <div className={styles.paginationInfo}>
            <span className="font-bold truncate">
              {currentPage} of {numberOfPages}{" "}
              {numberOfPages > 1 ? "pages" : "page"}
            </span>
          </div>
          <div className={styles.paginationInnerWrapper}>
            <div
              title="Navigate to first page"
              onClick={toFirstPage}
              className={styles.firstLastArrowContainer}
            >
              <FirstLastArrow className={styles.firstArrow} />
              <FirstLastArrow className={styles.firstArrow} />
            </div>
            <LeftArrow
              title="Navigate to previous page"
              onClick={toPrevPage}
              className={styles.leftRightArrow}
            />
            <ul className="flex space-x-10">
              {pageNumbers?.map((pageNumber) => (
                <li key={pageNumber}>
                  <button
                    type="button"
                    onClick={() => handlePageChange(pageNumber)}
                    className={clsx(
                      currentPage === pageNumber
                        ? styles.currentPage
                        : styles.otherPages,
                      styles.pageNumbersBase
                    )}
                  >
                    {pageNumber}
                  </button>
                </li>
              ))}
            </ul>
            <NextArrow
              title="Navigate to next page"
              onClick={toNextPage}
              className={styles.leftRightArrow}
            />
            <div
              title="Navigate to last page"
              onClick={toLastPage}
              className={styles.firstLastArrowContainer}
            >
              <FirstLastArrow className={styles.lastArrow} />
              <FirstLastArrow className={styles.lastArrow} />
            </div>
          </div>
        </nav>
      )}
    </>
  );
};

export default Pagination;
